/**
 * ID50 — 배송비조건 관리 FE 타입. (be-shipping 계약 — order-service docs/qa-fix/order-admin/ID50.md.)
 * 공급사 구분 × 배송방식 × 배송비유형의 설정 마스터 + 구간(BY_*) + 지역별 추가배송비.
 * 계산 resolver 는 seam(주문 미연동) — 본 화면은 설정 CRUD 까지.
 */

import type { RangeRepeat } from '@/lib/domain/rangeRepeat';

/** 공급사 구분. SUPPLIER 일 때만 supplierKey 가 의미를 가진다. */
export type SupplierScope = 'ALL' | 'HQ' | 'SUPPLIER';

export const SUPPLIER_SCOPES: ReadonlyArray<SupplierScope> = ['ALL', 'HQ', 'SUPPLIER'];

export const SUPPLIER_SCOPE_LABELS: Record<SupplierScope, string> = {
  ALL: '전체',
  HQ: '본사',
  SUPPLIER: '공급사',
};

/** 배송 방식. VISIT(방문수령) 은 배송비 0. QUICK(퀵 배송, QA-469)은 택배처럼 배송비를 매긴다(order-service 반영 요청). */
export type ShippingMethod = 'COURIER' | 'VISIT' | 'QUICK';

export const SHIPPING_METHODS: ReadonlyArray<ShippingMethod> = ['COURIER', 'VISIT', 'QUICK'];

export const SHIPPING_METHOD_LABELS: Record<ShippingMethod, string> = {
  COURIER: '택배',
  VISIT: '방문수령',
  QUICK: '퀵 배송',
};

/** 배송비 유형. BY_* 는 구간 테이블, FIXED 는 고정액, FREE 는 무료. */
export type ShippingFeeType = 'FIXED' | 'BY_AMOUNT' | 'BY_QTY' | 'BY_WEIGHT' | 'FREE';

export const SHIPPING_FEE_TYPES: ReadonlyArray<ShippingFeeType> = [
  'FIXED',
  'BY_AMOUNT',
  'BY_QTY',
  'BY_WEIGHT',
  'FREE',
];

export const SHIPPING_FEE_TYPE_LABELS: Record<ShippingFeeType, string> = {
  FIXED: '고정',
  BY_AMOUNT: '금액별',
  BY_QTY: '수량별',
  BY_WEIGHT: '무게별',
  FREE: '무료',
};

/** 구간형(BY_*) 여부. FIXED/FREE 는 구간 테이블 미사용. */
export const isTieredFeeType = (feeType: ShippingFeeType): boolean =>
  feeType === 'BY_AMOUNT' || feeType === 'BY_QTY' || feeType === 'BY_WEIGHT';

/**
 * 구간 단위 라벨(BY_* 별). minBound/maxBound/freeOverThreshold 의 의미를
 * 폼에서 사용자에게 안내한다.
 */
export const TIER_BOUND_UNIT: Record<'BY_AMOUNT' | 'BY_QTY' | 'BY_WEIGHT', string> = {
  BY_AMOUNT: '원',
  BY_QTY: '개',
  BY_WEIGHT: 'kg',
};

/** 결제 방법. CHOICE = 선불 or 착불 선택. */
export type PaymentMethod = 'PREPAID' | 'COD' | 'CHOICE';

export const PAYMENT_METHODS: ReadonlyArray<PaymentMethod> = ['PREPAID', 'COD', 'CHOICE'];

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  PREPAID: '선불',
  COD: '착불',
  CHOICE: '선불 or 착불',
};

/** 무제한 상한 sentinel — be-shipping 계약(999999999.99). */
export const UNLIMITED_MAX_BOUND = 999999999.99;

/**
 * 배송비 구간(ShippingFeeTier). BY_* 에서만 사용.
 * minBound/maxBound/freeOverThreshold 의 단위는 feeType 에 따라 금액/수량/무게.
 */
export interface ShippingFeeTier {
  /** 기존 행이면 서버 id, 신규면 undefined. */
  id?: string;
  minBound: number;
  /** 무제한 상한 = UNLIMITED_MAX_BOUND. */
  maxBound: number;
  fee: number;
  /** 측정값이 이 값 이상이면 0. null=미적용. */
  freeOverThreshold: number | null;
}

/** 지역별 추가배송비(ShippingFeeRegionSurcharge). regionSurchargeEnabled=true 에서만. */
export interface ShippingFeeRegionSurcharge {
  id?: string;
  /**
   * 지역명 — 매칭 기준(order V62). 배송지 주소에 들어 있으면 가산하고, 여러 지역이 맞으면 가장 구체적인
   * (긴) 1건만 적용한다. 필수, 최대 200자. 예: "제주특별자치도 제주시 추자면 대서3길".
   */
  label: string;
  /** (미사용) 예전 우편번호 앞자리 매칭 기준. 매칭에 쓰지 않는다 — 값이 오면 보존만 하고 입력은 받지 않는다. */
  postalPrefix?: string | null;
  surchargeFee: number;
}

/** 목록 행(ShippingFeePolicySummary — 그리드 컬럼). tiers/regionSurcharges 미포함. */
export interface ShippingFeePolicySummary {
  policyId: string;
  shippingFeeCode: string;
  name: string;
  supplierScope: SupplierScope;
  supplierKey: string | null;
  shippingMethod: ShippingMethod;
  feeType: ShippingFeeType;
  fixedFee: number;
  paymentMethod: PaymentMethod;
  regionSurchargeEnabled: boolean;
  /**
   * 기본 템플릿 여부(ID81, M-8). 전체 마스터에서 단 1개만 true — 신규 상품 등록 시
   * 자동 선택되는 템플릿. single-default 보장(true 설정 시 기존 default 해제)은
   * be-shipping(order V41) 트랜잭션이 enforce 한다. 응답에 없으면 false 로 간주.
   */
  isDefault: boolean;
  createdBy?: string | null;
  createdAt: string;
}

/** 상세(ShippingFeePolicyResponse). 구간 + 지역가산 포함. */
export interface ShippingFeePolicyDetail extends ShippingFeePolicySummary {
  tiers: ShippingFeeTier[];
  regionSurcharges: ShippingFeeRegionSurcharge[];
  /**
   * 범위반복(고도몰 rangeRepeat, order V61). 단위는 배송비 유형을 따른다(BY_AMOUNT 원 / BY_QTY 개 /
   * BY_WEIGHT kg). 반복이 없으면 응답에서 필드가 빠진다 — 없으면 반복 없음.
   */
  rangeRepeat?: RangeRepeat | null;
  updatedAt?: string;
}

/** POST/PUT body(ShippingFeePolicyUpsertRequest). shippingFeeCode 는 BE 자동생성이라 미포함. */
export interface ShippingFeePolicyPayload {
  name: string;
  supplierScope: SupplierScope;
  /** SUPPLIER 일 때만 의미. 그 외 null 정규화. */
  supplierKey: string | null;
  shippingMethod: ShippingMethod;
  feeType: ShippingFeeType;
  /** FIXED 일 때만 사용. 그 외 0 정규화. */
  fixedFee: number;
  paymentMethod: PaymentMethod;
  regionSurchargeEnabled: boolean;
  /**
   * 기본 템플릿 지정(M-8). true 면 신규 상품 자동선택 템플릿이 된다. single-default
   * (기존 default 자동해제)는 BE 트랜잭션이 처리하므로 BO 는 토글값만 전송한다.
   */
  isDefault: boolean;
  /** 구간형(BY_*) 에서만. FIXED/FREE 면 무시. */
  tiers: ShippingFeeTier[];
  /** regionSurchargeEnabled=true 에서만 저장. */
  regionSurcharges: ShippingFeeRegionSurcharge[];
  /**
   * 범위반복. 구간형(BY_*)에서만 저장되고 FIXED/FREE 면 BE 가 저장하지 않는다. ★PUT 이 전체 교체라
   * 유형과 무관하게 **항상** 보낸다 — 빼면 해제된다(구간과 같은 방식). null = 반복 없음.
   */
  rangeRepeat: RangeRepeat | null;
}

/** 목록 쿼리 파라미터. */
export interface ShippingFeePolicyListParams {
  /** 공급사 구분 필터. 미지정 = 전체. */
  supplierScope?: SupplierScope | null;
  /** 배송비코드/조건명/공급사키 부분일치. */
  keyword?: string | null;
  /** 1-base 페이지(화면). 훅에서 0-base 로 변환. */
  page?: number;
  size?: number;
}

/** 페이지네이션 결과 메타(meta.pagination). */
export interface ShippingFeePagination {
  page: number;
  size: number;
  totalItems: number;
  totalPages: number;
}

export interface ShippingFeePolicyPage {
  items: ShippingFeePolicySummary[];
  pagination: ShippingFeePagination;
}
